(() => {
  if (customElements.get('klay-home-edit')) return;

  class KlayHomeEdit extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;
      this.abortController = new AbortController();
      this.dialog = this.querySelector('[data-kh-dialog]');
      this.select = this.querySelector('[data-kh-variant]');
      this.preview = document.documentElement.dataset.klayPreview === 'true';
      const options = { signal: this.abortController.signal };
      const rail = this.querySelector('[data-kh-rail]');
      this.rail = rail;
      this.moreLink = this.querySelector('[data-kh-load-more]');
      const controls = this.querySelector('[data-kh-controls]');
      const previous = this.querySelector('[data-kh-prev]');
      const next = this.querySelector('[data-kh-next]');
      const updateRail = () => {
        const maximum = rail.scrollWidth - rail.clientWidth;
        controls.hidden = maximum < 2;
        previous.disabled = rail.scrollLeft <= 1;
        next.disabled = rail.scrollLeft >= maximum - 1 && this.moreLink.hidden;
      };
      this.updateRail = updateRail;
      const moveRail = direction => {
        const card = rail.querySelector('.kh-card');
        if (!card) return;
        const distance = card.getBoundingClientRect().width + parseFloat(getComputedStyle(rail).columnGap);
        rail.scrollBy({ left: direction * distance, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
      };
      previous.addEventListener('click', () => moveRail(-1), options);
      next.addEventListener('click', async () => {
        if (!this.moreLink.hidden && rail.scrollWidth - rail.clientWidth - rail.scrollLeft < 600) await this.loadMore();
        moveRail(1);
      }, options);
      rail.addEventListener('scroll', () => {
        updateRail();
        if (!this.moreLink.hidden && !this.loadFailed && rail.scrollWidth - rail.clientWidth - rail.scrollLeft < 600) this.loadMore();
      }, { ...options, passive: true });
      this.moreLink.addEventListener('click', event => { event.preventDefault(); this.loadMore(); }, options);
      this.resizeObserver = new ResizeObserver(updateRail);
      this.resizeObserver.observe(rail);
      updateRail();
      this.querySelectorAll('[data-kh-open]').forEach(button => { button.hidden = false; });
      this.addEventListener('click', event => {
        const opener = event.target.closest('[data-kh-open]');
        if (opener) this.openProduct(opener);
        if (event.target.closest('[data-kh-close]')) this.dialog.close();
        const size = event.target.closest('[data-kh-sizes] button');
        if (size && !size.disabled && !this.busy) {
          this.select.value = size.dataset.id;
          this.updateVariant();
        }
        const guideToggle = event.target.closest('[data-kh-guide-toggle]');
        if (guideToggle) {
          const guide = this.querySelector('[data-kh-guide]');
          guide.hidden = !guide.hidden;
          guideToggle.setAttribute('aria-expanded', String(!guide.hidden));
          const image = guide.querySelector('img');
          if (!guide.hidden && !image.getAttribute('src')) image.src = image.dataset.src;
        }
        const readMore = event.target.closest('[data-kh-read-more]');
        if (readMore) {
          const expanded = readMore.getAttribute('aria-expanded') !== 'true';
          this.querySelector('[data-kh-description-text]').textContent = expanded ? this.description : this.shortDescription;
          readMore.setAttribute('aria-expanded', String(expanded));
          readMore.textContent = expanded ? 'Read less' : 'Read more';
        }
      }, options);
      this.querySelector('[data-kh-sizes]').addEventListener('keydown', event => {
        if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key) || this.busy) return;
        const buttons = [...this.querySelectorAll('[data-kh-sizes] button:not(:disabled)')];
        if (!buttons.length) return;
        event.preventDefault();
        let index = buttons.indexOf(document.activeElement);
        if (event.key === 'Home') index = 0;
        else if (event.key === 'End') index = buttons.length - 1;
        else index = (index + (['ArrowLeft', 'ArrowUp'].includes(event.key) ? -1 : 1) + buttons.length) % buttons.length;
        buttons[index].click();
        buttons[index].focus();
      }, options);
      this.dialog.addEventListener('close', () => {
        this.unlockScroll();
        if (this.isConnected) this.opener?.focus({ preventScroll: true });
      }, options);
      this.dialog.addEventListener('click', event => {
        const rect = this.dialog.getBoundingClientRect();
        if (event.target === this.dialog && (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom)) this.dialog.close();
      }, options);
      this.querySelector('[data-kh-form]').addEventListener('submit', event => this.submitCart(event), options);
    }

    disconnectedCallback() {
      this.dialog?.close();
      this.unlockScroll();
      this.abortController?.abort();
      this.resizeObserver?.disconnect();
      this.abortController = null;
    }

    async loadMore() {
      if (this.moreLink.hidden || this.loadingMore) return this.loadingMore;
      const signal = this.abortController.signal;
      const status = this.querySelector('[data-kh-load-status]');
      const url = new URL(this.moreLink.href, location.origin);
      if (url.origin !== location.origin) return;
      url.searchParams.set('section_id', this.dataset.sectionId);
      this.moreLink.textContent = 'Loading shades…';
      this.moreLink.setAttribute('aria-disabled', 'true');
      this.rail.setAttribute('aria-busy', 'true');
      status.hidden = true;
      this.loadingMore = (async () => {
        try {
          const response = await fetch(url.href, { headers: { Accept: 'text/html' }, signal });
          if (!response.ok) throw new Error('Could not load more shades.');
          const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
          const section = doc.querySelector('klay-home-edit');
          const cards = section?.querySelectorAll('[data-kh-card]');
          if (!cards?.length) throw new Error('No additional products returned.');
          const ids = new Set([...this.rail.querySelectorAll('[data-kh-product]')].map(el => JSON.parse(el.textContent).id));
          const fresh = [...cards].filter(card => !ids.has(JSON.parse(card.querySelector('[data-kh-product]').textContent).id));
          if (!fresh.length) throw new Error('The next page repeated the current products.');
          fresh.forEach(card => {
            card.querySelectorAll('[data-kh-open]').forEach(button => { button.hidden = false; });
            this.rail.append(document.importNode(card, true));
          });
          const next = section.querySelector('[data-kh-load-more]');
          const nextHref = next?.getAttribute('href');
          this.moreLink.hidden = !nextHref || next.hidden;
          this.moreLink.setAttribute('href', nextHref || '');
          this.loadFailed = false;
          this.updateRail();
        } catch (error) {
          if (error.name === 'AbortError') return;
          this.loadFailed = true;
          status.textContent = 'More shades could not be loaded. Use “Load more shades” to try again.';
          status.hidden = false;
        } finally {
          this.moreLink.textContent = 'Load more shades';
          this.moreLink.removeAttribute('aria-disabled');
          this.rail.removeAttribute('aria-busy');
          this.loadingMore = null;
        }
      })();
      return this.loadingMore;
    }

    openProduct(opener) {
      if (this.busy) return;
      this.opener = opener;
      this.product = JSON.parse(opener.closest('[data-kh-card]').querySelector('[data-kh-product]').textContent);
      this.querySelector('[data-kh-title]').textContent = this.product.name;
      this.querySelector('[data-kh-error]').hidden = true;
      this.querySelector('[data-kh-status]').hidden = true;
      this.querySelector('[data-kh-sizes]').replaceChildren(...this.product.variants.map(variant => {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.id = String(variant.id);
        button.textContent = variant.title === 'Default Title' ? 'One size' : variant.title;
        button.setAttribute('role', 'radio');
        button.disabled = !variant.available;
        if (!variant.available) { button.setAttribute('aria-disabled', 'true'); button.title = 'Sold out'; }
        return button;
      }));
      const available = this.product.variants.find(variant => variant.available) || this.product.variants[0];
      this.select.value = available ? String(available.id) : '';
      const optionLabel = this.product.options?.length === 1 && this.product.options[0] !== 'Title' ? this.product.options[0] : 'Size';
      this.querySelector('[data-kh-option-label]').textContent = optionLabel;
      this.querySelector('[data-kh-sizes]').setAttribute('aria-label', optionLabel);
      const guideToggle = this.querySelector('[data-kh-guide-toggle]');
      if (guideToggle) {
        guideToggle.hidden = !this.product.variants.some(variant => /^(regular|mini)$/i.test(variant.title));
        guideToggle.setAttribute('aria-expanded', 'false');
        this.querySelector('[data-kh-guide]').hidden = true;
      }
      this.description = (this.product.description || '').replace(/\s+/g, ' ').trim();
      let cut = this.description.slice(0, 150);
      if (this.description.length > 150) cut = (cut.slice(0, cut.lastIndexOf(' ')) || cut) + '…';
      this.shortDescription = cut;
      this.querySelector('[data-kh-description-text]').textContent = cut;
      this.querySelector('[data-kh-description]').hidden = !this.description;
      const readMore = this.querySelector('[data-kh-read-more]');
      readMore.hidden = this.description.length <= 150;
      readMore.textContent = 'Read more';
      readMore.setAttribute('aria-expanded', 'false');
      const image = this.querySelector('[data-kh-image]');
      image.hidden = !this.product.images.length;
      if (this.product.images.length) this.showImage(this.product.images[0]);
      else { image.removeAttribute('src'); image.alt = ''; }
      this.querySelector('[data-kh-add]').hidden = this.preview;
      this.querySelector('[data-kh-continue]').hidden = !this.preview;
      this.querySelector('[data-kh-preview-note]').hidden = !this.preview;
      this.updateVariant();
      this.dialog.showModal();
      document.documentElement.classList.add('kh-dialog-lock');
      this.dialog.scrollTop = 0;
      this.querySelector('.kh-dialog__info').scrollTop = 0;
    }

    unlockScroll() {
      if (!document.querySelector('klay-home-edit dialog[open]')) document.documentElement.classList.remove('kh-dialog-lock');
    }

    showImage(image) {
      const element = this.querySelector('[data-kh-image]');
      element.src = image.src;
      element.alt = image.alt;
    }

    async submitCart(event) {
      event.preventDefault();
      if (this.preview) {
        window.location.assign(this.querySelector('[data-kh-continue]').href);
        return;
      }
      if (this.busy) return;
      const variant = this.product.variants.find(item => String(item.id) === this.select.value);
      if (!variant?.available) return;
      const button = this.querySelector('[data-kh-add]');
      const error = this.querySelector('[data-kh-error]');
      const status = this.querySelector('[data-kh-status]');
      error.hidden = true;
      status.hidden = true;
      this.busy = true;
      this.select.disabled = true;
      this.querySelectorAll('[data-kh-sizes] button').forEach(size => { size.disabled = true; });
      button.disabled = true;
      button.textContent = 'Adding…';
      let added = false;
      try {
        const response = await fetch(`${this.dataset.addUrl}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }], sections: ['mini-cart'], sections_url: window.location.pathname })
        });
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.description || payload.message || 'Unable to add this option. Please try another size.');
        added = true;
        const addedVariant = payload.items ? payload.items[0] : payload;
        document.documentElement.dispatchEvent(new CustomEvent('variant:added', { bubbles: true, detail: { variant: addedVariant } }));
        if (window.themeVariables?.settings?.cartType === 'page') {
          window.location.assign(this.dataset.cartUrl);
          return;
        }
        const cartResponse = await fetch(`${this.dataset.cartUrl}.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
        if (!cartResponse.ok) throw new Error('The bag could not be refreshed.');
        const cart = await cartResponse.json();
        if (payload.sections) cart.sections = payload.sections;
        this.dialog.close();
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart, openMiniCart: true } }));
      } catch (failure) {
        if (added) {
          status.replaceChildren(document.createTextNode('Added to your bag. '));
          const link = document.createElement('a');
          link.href = this.dataset.cartUrl;
          link.textContent = 'View your bag';
          status.append(link);
          status.hidden = false;
        } else {
          error.textContent = failure.message || 'Could not confirm this addition. Check your bag before trying again.';
          error.hidden = false;
        }
      } finally {
        this.busy = false;
        this.select.disabled = false;
        this.updateVariant();
      }
    }

    updateVariant() {
      const variant = this.product.variants.find(item => String(item.id) === this.select.value);
      const add = this.querySelector('[data-kh-add]');
      add.disabled = this.busy || !variant?.available;
      add.textContent = variant?.available ? 'Add to cart' : 'Sold out';
      this.querySelector('[data-kh-size]').textContent = variant?.title === 'Default Title' ? 'One size' : variant?.title || '';
      this.querySelectorAll('[data-kh-sizes] button').forEach(button => {
        const option = this.product.variants.find(item => String(item.id) === button.dataset.id);
        const selected = button.dataset.id === this.select.value;
        button.disabled = this.busy || !option?.available;
        button.setAttribute('aria-checked', String(selected));
        button.tabIndex = selected ? 0 : -1;
      });
      this.querySelector('[data-kh-price]').textContent = variant?.price || 'Unavailable';
      const url = new URL(this.product.url, this.preview ? 'https://klaythelabel.com' : window.location.origin);
      if (variant) url.searchParams.set('variant', variant.id);
      this.querySelector('[data-kh-continue]').href = url.href;
      this.querySelector('[data-kh-details]').href = url.href;
    }
  }

  customElements.define('klay-home-edit', KlayHomeEdit);
})();
